import {clearGACache} from "metabase/ab/utils/utils";
import {push, replace} from "react-router-redux";
import {getIn} from "icepick";
import {SessionApi, UtilApi} from "metabase/services";
import MetabaseSettings from "metabase/lib/settings";
import {createThunkAction} from "metabase/lib/redux";
import {loadLocalization} from "metabase/lib/i18n";
import {clearGoogleAuthCredentials, deleteSession} from "metabase/lib/auth";
import * as Urls from "metabase/lib/urls";
import {clearCurrentFgaProject, clearCurrentUser, refreshCurrentUser,} from "metabase/redux/user";
import {refreshSiteSettings} from "metabase/redux/settings";
import {getUser} from "metabase/selectors/user";
import {State} from "metabase-types/store";
import {trackPasswordReset,} from "./analytics";
import {LoginData} from "./types";
import * as MetabaseAnalytics from "metabase/lib/analytics";
import {trackStructEvent2} from "metabase/lib/analytics";
import {get} from "lodash";

export const REFRESH_LOCALE = "metabase/user/REFRESH_LOCALE";
export const refreshLocale = createThunkAction(
  REFRESH_LOCALE,
  () => async (dispatch: any, getState: () => State) => {
    const userLocale = getUser(getState())?.locale;
    const siteLocale = MetabaseSettings.get("site-locale");
    await loadLocalization(userLocale ?? siteLocale ?? "en");
  },
);

export const REFRESH_SESSION = "metabase/auth/REFRESH_SESSION";
export const refreshSession = createThunkAction(
  REFRESH_SESSION,
  () => async (dispatch: any) => {
    await Promise.all([
      dispatch(refreshCurrentUser()),
      dispatch(refreshSiteSettings()),
    ]);
    await dispatch(refreshLocale());
  },
);

const handleLogin = async (dispatch: any, redirectUrl: string, email: string) => {
  await Promise.all([
    dispatch(refreshCurrentUser()),
    dispatch(refreshSiteSettings()),
  ]);
  MetabaseAnalytics.trackStructEvent2("Login Events", "Login", email?.replace("@", "_"), 1, {
    "input_id": email?.replace("@", "_")
  });
  await delay(500);
  // dispatch(loadCurrentUserVip());
  // dispatch(loadCurrentUserVipDataApi());
  if (redirectUrl) {
    setTimeout(() => {
      dispatch(push(redirectUrl));
    }, 500);
  }
};

const delay = (duration: any) => new Promise((resolve) => setTimeout(resolve, duration));

export const LOGIN = "metabase/auth/LOGIN";
export const login = createThunkAction(
  LOGIN,
  (data: LoginData, redirectUrl: string) => async (dispatch: any) => {
    try {
      await SessionApi.create(data);
    } catch (error) {
      trackStructEvent2("Login Events", "LoginError", "login", 1, {
        error_msg: JSON.stringify(get(error, "data.errors")),
        input_id: data.username?.replace("@", "_"),
      })
      throw error;
    }
    await handleLogin(dispatch, redirectUrl, data.username);
  },
);

// register and login
export const REGISTERANDLOGIN = "metabase/auth/REGISTERANDLOGIN";
export const registerAndLogin = createThunkAction(
  REGISTERANDLOGIN,
  ({ token, redirectUrl }: any) =>
    async (dispatch: any, getState: any) => {
      // NOTE: this request will return a Set-Cookie header for the session
      // const { code, message, data } = await SessionApi.registerAndLogin({
      //   token,
      // });
      // if (code === 0) {
      //   MetabaseAnalytics.trackStructEvent("Auth", "registerAndLogin");
      //   await handleLogin(dispatch, redirectUrl || "/");
      // } else {
      //   throw message;
      // }
    },
);

export const LOGIN_GOOGLE = "metabase/auth/LOGIN_GOOGLE";
export const loginGoogle = createThunkAction(
  LOGIN_GOOGLE,
  (
      googleUser: any,
      redirectUrl: string,
      channel: string,
      projectRole: string,
    ) =>
    async (dispatch: any) => {
      try {
        const { data, code, message } = await SessionApi.createWithGoogleAuth({
          idToken: googleUser.credential,
          channel,
          ...(projectRole ? { projectRole } : {}),
        });
        if (code === -1) {
          return { code, message }
        }

        await handleLogin(dispatch, redirectUrl, googleUser?.profile?.getEmail());
      } catch (error) {
        await clearGoogleAuthCredentials();
        return error;
      }
    },
);

// login Wallet
export const LOGIN_WALLET = "metabase/auth/LOGIN_WALLET";
export const loginWallet = createThunkAction(
  LOGIN_WALLET,
  function (loginParam: any, redirectUrl: string) {
    return async function (dispatch: any, getState: any) {
      // try {
      //   const result = await WalletAddressLogin(loginParam);
      //   MetabaseAnalytics.trackStructEvent("Auth", "Wallet Auth Login");
      //   await handleLogin(dispatch, redirectUrl);
      //   return result;
      // } catch (error: any) {
      //   return { error: error.message ? error.message : error };
      // }
    };
  },
);

export const LOGOUT = "metabase/auth/LOGOUT";
export const logout = createThunkAction(LOGOUT, (redirectUrl: string) => {
  return async (dispatch: any, getState: any) => {
    const email = getState()?.currentUser?.email
    MetabaseAnalytics.trackStructEvent2("Login Events", "Logout", email?.replace("@", "_"), 1);
    await deleteSession();
    await dispatch(clearCurrentUser());
    await dispatch(clearCurrentFgaProject());
    await dispatch(refreshLocale());
    if (window.dataLayer) {
      window.dataLayer?.push({
        'event': 'userInfo',
      });
    }
    if (redirectUrl) {
      dispatch(push(Urls.login(redirectUrl)));
    } else {
      dispatch(replace("/auth/login"));
    }
    clearGACache();
    // window.location.reload(); // clears redux state and browser caches
  };
});

export const FORGOT_PASSWORD = "metabase/auth/FORGOT_PASSWORD";
export const forgotPassword = createThunkAction(
  FORGOT_PASSWORD,
  (email: string) => async () => {
    await SessionApi.forgot_password({ email });
  },
);

export const RESET_PASSWORD = "metabase/auth/RESET_PASSWORD";
export const resetPassword = createThunkAction(
  RESET_PASSWORD,
  (token: string, password: string) => async (dispatch: any) => {
    await SessionApi.reset_password({ token, password });
    await dispatch(refreshSession());
    trackPasswordReset();
  },
);

export const validatePassword = async (password: string) => {
  const error = MetabaseSettings.passwordComplexityDescription(password);
  if (error) {
    return error;
  }

  try {
    await UtilApi.password_check({ password });
  } catch (error) {
    return getIn(error, ["data", "errors", "password"]);
  }
};

export const VALIDATE_PASSWORD_TOKEN = "metabase/auth/VALIDATE_TOKEN";
export const validatePasswordToken = createThunkAction(
  VALIDATE_PASSWORD_TOKEN,
  (token: string) => async () => {
    const result = await SessionApi.password_reset_token_valid({ token });
    const valid = getIn(result, ["valid"]);

    if (!valid) {
      throw result;
    }
  },
);
